import { FC, Fragment } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import { Static } from './Static';

interface MetaProps {
  canonicalUrl: string;
  description: string;
  images: string[];
  indexable: boolean;
  pages: number;
  title: string;
}

const Meta: FC<MetaProps> = ({
  canonicalUrl = '/',
  description,
  images = [],
  indexable = false,
  pages,
  title,
}) => {
  const { query = {} } = useRouter();
  const { type, page } = query;

  const isSearchQuery = type === 'search'; // Don't include /s queries in this

  const currentPage: number = parseInt((page as string) || '1', 10);
  const currentPageText = `?page=${page}`;
  const prevPageText = `?page=${currentPage - 1}`;
  const nextPageText = `?page=${currentPage + 1}`;

  const canonical = `https://www.johnlewis.com/${canonicalUrl.replace(/^\//, '')}`;

  const currentPageCanonical = `${canonical}${
    currentPage > 1 ? currentPageText : ''
  }`;

  const RobotsMetaContent = () => {
    if (!indexable) {
      return <meta name="robots" content="noindex" />;
    }
  };

  const encodeHTML = (str) => {
    return str.replace(/[\u00A0-\u9999<>]/g, function (i) {
      return '&#' + i.charCodeAt(0) + ';';
    });
  };

  const encodedDescription = encodeHTML(description);

  const ServerTags = () => {
    if (typeof window === 'undefined') {
      return (
        <Fragment>
          <RobotsMetaContent />
          <meta
            key="metaDescription"
            name="description"
            content={encodedDescription}
          />
          <meta
            key="metaOgSiteName"
            property="og:site_name"
            content="John Lewis & Partners"
          />
          <meta property="og:url" content={currentPageCanonical} />
          {images.length > 0 ? (
            <Fragment>
              {images.map((image) => (
                <meta key={image} property="og:image" content={image} />
              ))}
              <meta property="og:image:type" content="image/jpeg" />
            </Fragment>
          ) : (
            <Fragment>
              <meta
                property="og:image"
                content="https://media.johnlewiscontent.com/i/JohnLewis/ogFeatureImage-johnLewisPartners-040918"
              />
              <meta property="og:image:type" content="image/png" />
            </Fragment>
          )}
          <meta property="og:type" content="website" />
          <meta property="og:locale" content="en_GB" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={encodedDescription} />

          <Static />
        </Fragment>
      );
    }
  };

  return (
    <Head>
      <title>{title}</title>
      <ServerTags />
      {!isSearchQuery && (
        <Fragment>
          <link rel="canonical" href={currentPageCanonical} />
          {currentPage < pages && (
            <link rel="next" href={`${canonical}${nextPageText}`} />
          )}
          {currentPage > 1 && (
            <link
              rel="prev"
              href={`${canonical}${currentPage > 2 ? `${prevPageText}` : ''}`}
            />
          )}
        </Fragment>
      )}
    </Head>
  );
};

Meta.propTypes = {
  canonicalUrl: PropTypes.string,
  description: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(PropTypes.string),
  indexable: PropTypes.bool,
  pages: PropTypes.number,
  title: PropTypes.string.isRequired,
};

export default Meta;
